import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Grid3X3, Filter, MapPin, RefreshCw, Zap, BadgeCheck, ChevronDown, ChevronUp, X, Sparkles } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sample profile images for demo users
const sampleImages = [
  'https://images.unsplash.com/photo-1635631191842-cc5043d56fc4?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  'https://images.unsplash.com/photo-1762708546140-7aea0b34ecac?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  'https://images.unsplash.com/photo-1582439170934-d089aa10abda?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  'https://images.unsplash.com/photo-1635631191694-8fc637d53429?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  'https://images.unsplash.com/photo-1666819862436-0afec2604732?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  'https://images.unsplash.com/photo-1747442318345-05eb86983b50?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  'https://images.unsplash.com/photo-1762191854936-edfc07615112?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  'https://images.unsplash.com/photo-1685328403755-de1d57e12e63?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'
];

const Grid = () => {
  const navigate = useNavigate();
  const { user, updateLocation } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [filters, setFilters] = useState({
    // Basic filters
    maxDistance: 50,
    minAge: 18,
    maxAge: 99,
    hideNoPhoto: false,
    hideNewUsers: false,
    // Identity filters
    gender: '',
    sexuality: '',
    position: '',
    // Stats filters
    bodyType: '',
    ethnicity: '',
    tribe: [],
    relationshipStatus: '',
    // Health filters
    hivStatus: '',
    preventionPractices: [],
    // Kinks filters
    kinksFreaks: [],
    // Looking for
    lookingFor: []
  });

  // Fetch dropdown options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await axios.get(`${API}/dropdown-options`);
        setDropdownOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch dropdown options:', error);
      }
    };
    fetchOptions();
  }, []);

  // Count active filters
  useEffect(() => {
    let count = 0;
    if (filters.gender) count++;
    if (filters.sexuality) count++;
    if (filters.position) count++;
    if (filters.bodyType) count++;
    if (filters.ethnicity) count++;
    if (filters.tribe.length > 0) count++;
    if (filters.relationshipStatus) count++;
    if (filters.hivStatus) count++;
    if (filters.preventionPractices.length > 0) count++;
    if (filters.kinksFreaks.length > 0) count++;
    if (filters.lookingFor.length > 0) count++;
    if (filters.hideNoPhoto) count++;
    if (filters.hideNewUsers) count++;
    setActiveFilterCount(count);
  }, [filters]);

  const fetchNearbyUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.maxDistance) params.append('max_distance', filters.maxDistance);
      if (filters.minAge) params.append('min_age', filters.minAge);
      if (filters.maxAge) params.append('max_age', filters.maxAge);
      if (filters.lookingFor.length > 0) params.append('looking_for', filters.lookingFor.join(','));
      if (filters.bodyType) params.append('body_type', filters.bodyType);
      if (filters.position) params.append('position', filters.position);
      if (filters.tribe.length > 0) params.append('tribe', filters.tribe.join(','));
      if (filters.hideNoPhoto) params.append('hide_no_photo', 'true');
      if (filters.hideNewUsers) params.append('hide_new_users', 'true');
      // New filters
      if (filters.gender) params.append('gender', filters.gender);
      if (filters.sexuality) params.append('sexuality', filters.sexuality);
      if (filters.ethnicity) params.append('ethnicity', filters.ethnicity);
      if (filters.relationshipStatus) params.append('relationship_status', filters.relationshipStatus);
      if (filters.hivStatus) params.append('hiv_status', filters.hivStatus);
      if (filters.preventionPractices.length > 0) params.append('prevention_practices', filters.preventionPractices.join(','));
      if (filters.kinksFreaks.length > 0) params.append('kinks_freaks', filters.kinksFreaks.join(','));

      const response = await axios.get(`${API}/users/nearby?${params.toString()}`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 403) {
        toast.error('Advanced filters require Premium membership');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const clearAllFilters = () => {
    setFilters({
      maxDistance: 50,
      minAge: 18,
      maxAge: 99,
      hideNoPhoto: false,
      hideNewUsers: false,
      gender: '',
      sexuality: '',
      position: '',
      bodyType: '',
      ethnicity: '',
      tribe: [],
      relationshipStatus: '',
      hivStatus: '',
      preventionPractices: [],
      kinksFreaks: [],
      lookingFor: []
    });
  };

  const requestLocation = useCallback(async () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            await updateLocation(position.coords.latitude, position.coords.longitude);
            fetchNearbyUsers();
          } catch (error) {
            console.error('Failed to update location:', error);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Enable location to see people nearby');
          fetchNearbyUsers();
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      fetchNearbyUsers();
    }
  }, [updateLocation, fetchNearbyUsers]);

  useEffect(() => {
    requestLocation();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchNearbyUsers();
    }
  }, [filters]);

  const handleRefresh = () => {
    setLoading(true);
    requestLocation();
  };

  const applyFilters = () => {
    setShowFilters(false);
    setLoading(true);
    fetchNearbyUsers();
  };

  const getUserImage = (user, index) => {
    if (user.public_photos && user.public_photos.length > 0) {
      const photo = user.public_photos[0];
      if (photo.startsWith('data:') || photo.startsWith('http')) {
        return photo;
      }
      return `data:image/jpeg;base64,${photo}`;
    }
    return sampleImages[index % sampleImages.length];
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-xl font-bold text-[#FF2E63]">Browse</h1>
          <div className="flex items-center gap-1">
            <Button
              data-testid="refresh-grid-btn"
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              data-testid="filter-btn"
              variant="ghost"
              size="icon"
              onClick={() => setShowFilters(true)}
              className="text-[#A1A1AA] hover:text-white hover:bg-white/10 relative"
            >
              <Filter className="w-5 h-5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#FF2E63] rounded-full text-[10px] font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* User Grid */}
      <main className="user-grid p-1">
        {loading ? (
          // Skeleton loading
          Array(12).fill(0).map((_, i) => (
            <div key={i} className="aspect-square skeleton rounded-sm" />
          ))
        ) : users.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
            <MapPin className="w-12 h-12 text-[#52525B] mb-4" />
            <h3 className="text-lg font-semibold text-[#EDEDED] mb-2">No users nearby</h3>
            <p className="text-sm text-[#A1A1AA] mb-4">Try expanding your search distance or adjusting filters</p>
            <Button
              onClick={() => setShowFilters(true)}
              className="bg-[#FF2E63] hover:bg-[#E01E50] text-white"
            >
              Adjust Filters
            </Button>
          </div>
        ) : (
          users.map((nearbyUser, index) => (
            <div
              key={nearbyUser.id}
              data-testid={`user-card-${nearbyUser.id}`}
              className="user-card cursor-pointer group"
              onClick={() => navigate(`/profile/${nearbyUser.id}`)}
            >
              <img
                src={getUserImage(nearbyUser, index)}
                alt={nearbyUser.display_name || nearbyUser.username}
                className="w-full h-full object-cover"
              />
              {/* Online indicator */}
              {nearbyUser.is_online && (
                <div className="absolute top-2 right-2 online-indicator z-10" />
              )}
              {/* Tap indicator - shows if they tapped you */}
              {nearbyUser.has_tapped_me && (
                <div className="absolute top-2 left-2 z-10 w-6 h-6 bg-[#FF2E63] rounded-full flex items-center justify-center shadow-lg">
                  <Zap className="w-4 h-4 text-white" />
                </div>
              )}
              {/* Availability status indicator */}
              {nearbyUser.availability_status === 'available_now' && (
                <div className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-[#22c55e] rounded-full flex items-center gap-1 shadow-lg">
                  <Zap className="w-3 h-3 text-white" />
                  <span className="text-[10px] text-white font-bold">NOW</span>
                </div>
              )}
              {nearbyUser.availability_status === 'ready_to_meet' && !nearbyUser.has_tapped_me && (
                <div className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-[#08D9D6] rounded-full">
                  <span className="text-[10px] text-black font-bold">READY</span>
                </div>
              )}
              {/* User info overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-2 z-10">
                <div className="flex items-center gap-1">
                  <p className="text-white text-sm font-semibold truncate">
                    {nearbyUser.display_name || nearbyUser.username}
                    {nearbyUser.age && <span className="font-normal">, {nearbyUser.age}</span>}
                  </p>
                  {nearbyUser.is_verified && (
                    <BadgeCheck className="w-4 h-4 text-[#08D9D6] flex-shrink-0" />
                  )}
                </div>
                {nearbyUser.distance !== null && nearbyUser.distance !== undefined && (
                  <p className="text-[#A1A1AA] text-xs font-mono">
                    {nearbyUser.distance < 1 ? '< 1' : nearbyUser.distance} km away
                  </p>
                )}
              </div>
            </div>
          ))
        )}
      </main>

      <BottomNav />

      {/* Filters Modal */}
      <Dialog open={showFilters} onOpenChange={setShowFilters}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-hidden p-0">
          <DialogHeader className="px-4 pt-4 pb-2 border-b border-white/10">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-bold flex items-center gap-2">
                <Filter className="w-5 h-5 text-[#FF2E63]" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-[#FF2E63] text-white text-xs">
                    {activeFilterCount}
                  </span>
                )}
              </DialogTitle>
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-[#A1A1AA] hover:text-white text-xs"
                >
                  Clear All
                </Button>
              )}
            </div>
          </DialogHeader>

          <Tabs defaultValue="basic" className="flex-1">
            <TabsList className="w-full justify-start px-4 py-2 bg-transparent border-b border-white/10 rounded-none gap-0">
              <TabsTrigger value="basic" className="text-xs data-[state=active]:bg-[#FF2E63]/20 data-[state=active]:text-[#FF2E63]">Basic</TabsTrigger>
              <TabsTrigger value="identity" className="text-xs data-[state=active]:bg-[#FF2E63]/20 data-[state=active]:text-[#FF2E63]">Identity</TabsTrigger>
              <TabsTrigger value="stats" className="text-xs data-[state=active]:bg-[#FF2E63]/20 data-[state=active]:text-[#FF2E63]">Stats</TabsTrigger>
              <TabsTrigger value="health" className="text-xs data-[state=active]:bg-[#FF2E63]/20 data-[state=active]:text-[#FF2E63]">Health</TabsTrigger>
              <TabsTrigger value="kinks" className="text-xs data-[state=active]:bg-[#7000FF]/20 data-[state=active]:text-[#7000FF]">
                <Sparkles className="w-3 h-3 mr-1" />
                Kinks
              </TabsTrigger>
            </TabsList>

            <div className="overflow-y-auto max-h-[calc(85vh-180px)] px-4 py-4">
              {/* Basic Tab */}
              <TabsContent value="basic" className="space-y-5 mt-0">
                {/* Distance */}
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <Label className="text-[#EDEDED]">Max Distance</Label>
                    <span className="text-sm text-[#FF2E63] font-mono">{filters.maxDistance} km</span>
                  </div>
                  <Slider
                    value={[filters.maxDistance]}
                    onValueChange={([value]) => setFilters(f => ({ ...f, maxDistance: value }))}
                    min={1}
                    max={100}
                    step={1}
                    className="[&_[role=slider]]:bg-[#FF2E63]"
                  />
                </div>

                {/* Age Range */}
                <div className="space-y-3">
                  <Label className="text-[#EDEDED]">Age Range</Label>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Select
                        value={filters.minAge.toString()}
                        onValueChange={(value) => setFilters(f => ({ ...f, minAge: parseInt(value) }))}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          {Array.from({ length: 82 }, (_, i) => i + 18).map(age => (
                            <SelectItem key={age} value={age.toString()}>{age}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <span className="text-[#52525B]">to</span>
                    <div className="flex-1">
                      <Select
                        value={filters.maxAge.toString()}
                        onValueChange={(value) => setFilters(f => ({ ...f, maxAge: parseInt(value) }))}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          {Array.from({ length: 82 }, (_, i) => i + 18).map(age => (
                            <SelectItem key={age} value={age.toString()}>{age}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Looking For - Multi-select */}
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Looking For</Label>
                  <div className="flex flex-wrap gap-2">
                    {dropdownOptions.looking_for?.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          const current = filters.lookingFor;
                          if (current.includes(opt)) {
                            setFilters(f => ({ ...f, lookingFor: current.filter(i => i !== opt) }));
                          } else {
                            setFilters(f => ({ ...f, lookingFor: [...current, opt] }));
                          }
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                          filters.lookingFor.includes(opt)
                            ? 'bg-[#FF2E63]/20 border border-[#FF2E63] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quality Filters */}
                <div className="space-y-3 pt-2">
                  <Label className="text-[#A1A1AA] text-xs">Quality Filters</Label>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#EDEDED]">Hide profiles without photos</span>
                    <Switch
                      checked={filters.hideNoPhoto}
                      onCheckedChange={(checked) => setFilters(f => ({ ...f, hideNoPhoto: checked }))}
                      className="data-[state=checked]:bg-[#FF2E63]"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#EDEDED]">Hide new users (7 days)</span>
                    <Switch
                      checked={filters.hideNewUsers}
                      onCheckedChange={(checked) => setFilters(f => ({ ...f, hideNewUsers: checked }))}
                      className="data-[state=checked]:bg-[#FF2E63]"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Identity Tab */}
              <TabsContent value="identity" className="space-y-4 mt-0">
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Gender</Label>
                  <Select
                    value={filters.gender || "any"}
                    onValueChange={(value) => setFilters(f => ({ ...f, gender: value === "any" ? "" : value }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      <SelectItem value="any">Any</SelectItem>
                      {dropdownOptions.genders?.map(g => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Sexuality</Label>
                  <Select
                    value={filters.sexuality || "any"}
                    onValueChange={(value) => setFilters(f => ({ ...f, sexuality: value === "any" ? "" : value }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="any">Any</SelectItem>
                      {dropdownOptions.sexuality?.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Position</Label>
                  <Select
                    value={filters.position || "any"}
                    onValueChange={(value) => setFilters(f => ({ ...f, position: value === "any" ? "" : value }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="any">Any</SelectItem>
                      {dropdownOptions.positions?.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              {/* Stats Tab */}
              <TabsContent value="stats" className="space-y-4 mt-0">
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Body Type</Label>
                  <Select
                    value={filters.bodyType || "any"}
                    onValueChange={(value) => setFilters(f => ({ ...f, bodyType: value === "any" ? "" : value }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="any">Any</SelectItem>
                      {dropdownOptions.body_types?.map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Ethnicity</Label>
                  <Select
                    value={filters.ethnicity || "any"}
                    onValueChange={(value) => setFilters(f => ({ ...f, ethnicity: value === "any" ? "" : value }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      <SelectItem value="any">Any</SelectItem>
                      {dropdownOptions.ethnicities?.map(e => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Relationship Status</Label>
                  <Select
                    value={filters.relationshipStatus || "any"}
                    onValueChange={(value) => setFilters(f => ({ ...f, relationshipStatus: value === "any" ? "" : value }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="any">Any</SelectItem>
                      {dropdownOptions.relationship_status?.map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tribe - Multi-select */}
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Tribe</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {dropdownOptions.tribes?.map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => {
                          const current = filters.tribe;
                          if (current.includes(t)) {
                            setFilters(f => ({ ...f, tribe: current.filter(i => i !== t) }));
                          } else {
                            setFilters(f => ({ ...f, tribe: [...current, t] }));
                          }
                        }}
                        className={`px-2 py-1 rounded text-xs capitalize transition-all ${
                          filters.tribe.includes(t)
                            ? 'bg-[#FF2E63]/20 border border-[#FF2E63] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Health Tab */}
              <TabsContent value="health" className="space-y-4 mt-0">
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">HIV Status</Label>
                  <Select
                    value={filters.hivStatus || "any"}
                    onValueChange={(value) => setFilters(f => ({ ...f, hivStatus: value === "any" ? "" : value }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="any">Any</SelectItem>
                      {dropdownOptions.hiv_status?.map(h => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Prevention Practices - Multi-select */}
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Prevention Practices</Label>
                  <div className="flex flex-wrap gap-2">
                    {dropdownOptions.prevention_practices?.map(p => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => {
                          const current = filters.preventionPractices;
                          if (current.includes(p)) {
                            setFilters(f => ({ ...f, preventionPractices: current.filter(i => i !== p) }));
                          } else {
                            setFilters(f => ({ ...f, preventionPractices: [...current, p] }));
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                          filters.preventionPractices.includes(p)
                            ? 'bg-[#08D9D6]/20 border border-[#08D9D6] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Kinks Tab */}
              <TabsContent value="kinks" className="space-y-4 mt-0">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#EDEDED]">Kinks & Freaks</Label>
                    {filters.kinksFreaks.length > 0 && (
                      <span className="text-xs text-[#7000FF]">{filters.kinksFreaks.length} selected</span>
                    )}
                  </div>
                  <p className="text-xs text-[#52525B]">Match users who share at least one of your selected interests</p>
                  <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto">
                    {dropdownOptions.kinks_freaks?.map(k => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => {
                          const current = filters.kinksFreaks;
                          if (current.includes(k)) {
                            setFilters(f => ({ ...f, kinksFreaks: current.filter(i => i !== k) }));
                          } else {
                            setFilters(f => ({ ...f, kinksFreaks: [...current, k] }));
                          }
                        }}
                        className={`px-2 py-1 rounded text-xs transition-all ${
                          filters.kinksFreaks.includes(k)
                            ? 'bg-[#7000FF]/20 border border-[#7000FF] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <div className="flex gap-3 p-4 border-t border-white/10">
            <Button
              variant="ghost"
              onClick={() => setShowFilters(false)}
              className="flex-1 text-[#A1A1AA] hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={applyFilters}
              className="flex-1 bg-[#FF2E63] hover:bg-[#E01E50] text-white"
            >
              Apply Filters
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Grid;
